import React, { useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { ArrowRight, Shield, Check, X } from 'lucide-react';
import TurnstileWidget from '@/components/TurnstileWidget';

const PASSWORD_RULES = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'One number', test: (p) => /\d/.test(p) },
  { label: 'One special character', test: (p) => /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'/~`]/.test(p) },
];

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [honeypot] = useState('');
  const [showPwRules, setShowPwRules] = useState(false);

  const onCaptchaVerify = useCallback((token) => setCaptchaToken(token), []);
  const onCaptchaExpire = useCallback(() => setCaptchaToken(''), []);

  const passwordStrength = useMemo(() => {
    const passed = PASSWORD_RULES.filter(r => r.test(form.password)).length;
    return { passed, total: PASSWORD_RULES.length, percent: (passed / PASSWORD_RULES.length) * 100 };
  }, [form.password]);

  const strengthColor = passwordStrength.percent === 100 ? 'bg-green-500' :
    passwordStrength.percent >= 60 ? 'bg-yellow-500' : 'bg-red-400';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!captchaToken) { setError('Please complete the CAPTCHA verification'); return; }
    if (passwordStrength.passed < passwordStrength.total) {
      setError('Please meet all password requirements'); return;
    }
    setLoading(true);
    const result = await register(form.name, form.email, form.password, form.phone, captchaToken, honeypot);
    setLoading(false);
    if (result.success) { navigate('/dashboard'); } else { setError(result.error); }
  };

  const update = (key, val) => setForm(p => ({ ...p, [key]: val }));

  return (
    <div className="min-h-screen bg-[#FFFEF9] flex items-center justify-center px-4 py-16" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-12 h-12 rounded-lg overflow-hidden flex items-center justify-center">
              <img src="/bb-logo.jpeg" alt="BuildBotics" className="w-full h-full object-contain" />
            </div>
          </Link>
          <h1 className="text-2xl font-bold text-[#1a1a2e]" style={{ fontFamily: 'Outfit' }}>Create your account</h1>
          <p className="text-sm text-gray-500 mt-1">Start your robotics journey today</p>
        </div>

        <form onSubmit={handleSubmit} className="neo-card-static p-8 space-y-5" data-testid="register-form">
          {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg" data-testid="register-error">{error}</div>}
          {/* Honeypot */}
          <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
            <input type="text" name="fax_number" tabIndex={-1} autoComplete="new-password"
              value="" onChange={() => {}} readOnly />
          </div>
          <div>
            <label className="text-sm font-medium text-[#1a1a2e] mb-1.5 block">Full Name</label>
            <Input value={form.name} onChange={e => update('name', e.target.value)} placeholder="Your full name" required
              className="bg-[#FEFCE8] border-gray-200 text-[#1a1a2e] focus:border-[#0891B2] focus:ring-[#0891B2] h-11" data-testid="register-name-input" />
          </div>
          <div>
            <label className="text-sm font-medium text-[#1a1a2e] mb-1.5 block">Email</label>
            <Input type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="you@email.com" required
              className="bg-[#FEFCE8] border-gray-200 text-[#1a1a2e] focus:border-[#0891B2] focus:ring-[#0891B2] h-11" data-testid="register-email-input" />
          </div>
          <div>
            <label className="text-sm font-medium text-[#1a1a2e] mb-1.5 block">Phone (optional)</label>
            <Input value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+91 99999 99999"
              className="bg-[#FEFCE8] border-gray-200 text-[#1a1a2e] focus:border-[#0891B2] focus:ring-[#0891B2] h-11" data-testid="register-phone-input" />
          </div>
          <div>
            <label className="text-sm font-medium text-[#1a1a2e] mb-1.5 block">Password</label>
            <Input type="password" value={form.password}
              onChange={e => update('password', e.target.value)}
              onFocus={() => setShowPwRules(true)}
              placeholder="Min. 8 characters, uppercase, number, special"
              required
              className="bg-[#FEFCE8] border-gray-200 text-[#1a1a2e] focus:border-[#0891B2] focus:ring-[#0891B2] h-11" data-testid="register-password-input" />
            {/* Password strength bar */}
            {form.password && (
              <div className="mt-2" data-testid="password-strength">
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div className={`h-full ${strengthColor} transition-all duration-300 rounded-full`}
                    style={{ width: `${passwordStrength.percent}%` }} />
                </div>
                <span className={`text-xs mt-1 block ${passwordStrength.percent === 100 ? 'text-green-600' : passwordStrength.percent >= 60 ? 'text-yellow-600' : 'text-red-500'}`}>
                  {passwordStrength.percent === 100 ? 'Strong password' : passwordStrength.percent >= 60 ? 'Almost there' : 'Weak password'}
                </span>
              </div>
            )}
            {/* Password rules checklist */}
            {showPwRules && form.password && (
              <div className="mt-2 space-y-1" data-testid="password-rules">
                {PASSWORD_RULES.map((r, i) => {
                  const pass = r.test(form.password);
                  return (
                    <div key={i} className={`flex items-center gap-2 text-xs ${pass ? 'text-green-600' : 'text-gray-400'}`}>
                      {pass ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      <span>{r.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <TurnstileWidget onVerify={onCaptchaVerify} onExpire={onCaptchaExpire} />
          <button type="submit" disabled={loading} className="btn-cyan w-full flex items-center justify-center gap-2 text-sm !rounded-xl" data-testid="register-form-submit-button">
            {loading ? 'Creating account...' : <><Shield className="w-4 h-4" /><span>CREATE ACCOUNT</span> <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-[#0891B2] hover:underline font-medium" data-testid="register-login-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
